import { Fragment, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import PrimaryButton from '../Buttons/PrimaryButton';
import { useMutation } from 'react-query';
import { bulkUpdateProfiles } from '../../api/profile';
import { useAuth } from '../../contexts/Auth';
import { toast } from 'react-toastify';

export type PinYears = '10' | '25' | '50';

type DeliverPinVars = { profileIds: number[]; pinYears: PinYears };

/** Risposta PUT `/profiles/bulk-update` (stessi profili aggiornati dal server). */
export type BulkUpdateProfilesResponse = {
  data: Record<string, unknown>[];
};

interface ConsegnaSpillaModalProps {
  selectedIds: number[];
  /** Dopo l’aggiornamento: payload con i profili aggiornati per sincronizzare la cache lista. */
  onSuccess: (result: BulkUpdateProfilesResponse) => void | Promise<void>;
}

const pinFieldByYears: Record<PinYears, string> = {
  '10': 'pin10YearsDelivered',
  '25': 'pin25YearsDelivered',
  '50': 'pin50YearsDelivered',
};

const ConsegnaSpillaModal: React.FC<ConsegnaSpillaModalProps> = ({
  selectedIds,
  onSuccess,
}) => {
  const [{ token }] = useAuth();
  const [open, setOpen] = useState(false);
  const [pinYears, setPinYears] = useState<PinYears>('10');

  const { mutateAsync, isLoading } = useMutation(
    ({ profileIds, pinYears: years }: DeliverPinVars) =>
      bulkUpdateProfiles({
        token,
        body: {
          profileIds,
          data: { [pinFieldByYears[years]]: true },
        },
      })
  );

  const handleConfirm = async () => {
    if (selectedIds.length === 0) return;
    try {
      const result = (await mutateAsync({
        profileIds: [...selectedIds],
        pinYears,
      })) as BulkUpdateProfilesResponse;
      toast.success('Consegna spilla registrata');
      setOpen(false);
      await onSuccess(result);
    } catch {
      toast.error("Qualcosa è andato storto durante l'aggiornamento.");
    }
  };

  return (
    <>
      <PrimaryButton
        textSmall
        disabled={selectedIds.length === 0}
        onClick={() => setOpen(true)}
      >
        <span className='text-sm underline'>Consegna spilla</span>
      </PrimaryButton>
      <Transition.Root show={open} as={Fragment}>
        <Dialog as='div' className='fixed inset-0 z-10 overflow-y-auto' onClose={setOpen}>
          <div className='flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0'>
            <Transition.Child
              as={Fragment}
              enter='ease-out duration-300'
              enterFrom='opacity-0'
              enterTo='opacity-100'
              leave='ease-in duration-200'
              leaveFrom='opacity-100'
              leaveTo='opacity-0'
            >
              <Dialog.Overlay className='fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75' />
            </Transition.Child>
            <span
              className='hidden sm:inline-block sm:align-middle sm:h-screen'
              aria-hidden='true'
            >
              &#8203;
            </span>
            <Transition.Child
              as={Fragment}
              enter='ease-out duration-300'
              enterFrom='opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95'
              enterTo='opacity-100 translate-y-0 sm:scale-100'
              leave='ease-in duration-200'
              leaveFrom='opacity-100 translate-y-0 sm:scale-100'
              leaveTo='opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95'
            >
              <div className='relative inline-block px-4 pt-5 pb-4 overflow-hidden text-left align-bottom transition-all transform bg-white rounded-lg shadow-xl sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6'>
                <h4 className='text-1.5xl font-IBM font-light text-gray-700 mb-4'>
                  Consegna spilla
                </h4>
                <p className='text-sm text-gray-600 mb-4'>
                  Stai aggiornando {selectedIds.length} utent
                  {selectedIds.length === 1 ? 'e' : 'i'}. Seleziona quale spilla
                  è stata consegnata.
                </p>
                <div className='space-y-3 mb-6'>
                  {(
                    [
                      { value: '10' as const, label: 'Spilla 10 anni' },
                      { value: '25' as const, label: 'Spilla 25 anni' },
                      { value: '50' as const, label: 'Spilla 50 anni' },
                    ] as const
                  ).map((opt) => (
                    <label
                      key={opt.value}
                      className='flex items-center gap-2 cursor-pointer text-sm text-gray-700'
                    >
                      <input
                        type='radio'
                        name='pinYears'
                        value={opt.value}
                        checked={pinYears === opt.value}
                        onChange={() => setPinYears(opt.value)}
                        className='h-4 w-4 text-primary border-gray-300 focus:ring-primary'
                      />
                      {opt.label}
                    </label>
                  ))}
                </div>
                <div className='flex items-center justify-end gap-4'>
                  <PrimaryButton textSmall onClick={() => setOpen(false)}>
                    Annulla
                  </PrimaryButton>
                  <PrimaryButton
                    disabled={isLoading || selectedIds.length === 0}
                    isLoading={isLoading}
                    small
                    onClick={() => void handleConfirm()}
                  >
                    Conferma
                  </PrimaryButton>
                </div>
              </div>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  );
};

export default ConsegnaSpillaModal;
